import { useEffect, useState } from "react";
import { paste } from "utils/Helpers";
import { useAsset } from "provider/AssetProvider";
import { ConfigResponse } from "models/staking/ConfigResponse";
import { showLoadingToast, showTxToast } from "utils/Toasts";
import { querySmartContract } from "utils/RpcHelpers";
import { useWallet } from "provider/WalletProvider";
import { MsgExecuteContract } from "@goblinhunt/cosmes/client";

export function StakingConfigPage() {
    const { address: sender, post } = useWallet()

    const { tokenAddress: address, tokenInfo, stakingAddress } = useAsset();
    const [apr, setApr] = useState('10');
    const [duration, setDuration] = useState('0');
    const [ownerAddress, setOwnerAddress] = useState('')

    const loadConfig = async () => {
        try {
            const { config }: ConfigResponse = await querySmartContract(stakingAddress!, { config: {} })
            setApr((Number(config.apr) * 100).toString())
            setDuration(config.unstake_duration.toString())
            setOwnerAddress(config.owner)
        } catch (e) {
            await showLoadingToast("Load staking config", async () => { throw e })
        }
    }

    useEffect(() => {
        if (stakingAddress) loadConfig()
    }, [stakingAddress])

    const onRegister = async () => {
        const setConfigMsg = {
            set_config: {
                apr: (Number(apr) / 100).toString(),
                unstake_duration: Number(duration),
                owner: ownerAddress
            }
        }

        const executeMsg = new MsgExecuteContract({
            sender: sender!,
            contract: stakingAddress!,
            msg: setConfigMsg,
            funds: []
        })
        const msgs = [executeMsg];

        const result = await showLoadingToast("Signing transaction", async () => await post({
            msgs,
        }))
        await showTxToast(result)
    }

    const isValid = () => {
        if (!sender) {
            return "Please connect your wallet first"
        }

        if (!tokenInfo || !stakingAddress) {
            return "Please select an asset"
        }

        if (isNaN(Number(apr) / 100) || Number(apr) < 0) {
            return "APR must be a percentage number, use . (period) as decimal separator"
        }

        if (isNaN(Number(duration)) || Number(duration) <= 0) {
            return "Duration must be more than or equals 1 day"
        }

        if (!ownerAddress) {
            return "Please enter owner wallet address"
        }

        return undefined;
    }


    return <div className="container-fluid flex-grow-1 container-p-y">
        <div className="row">
            <div className="col-md-6 col-12 mb-md-0 mb-4">
                <div className="card">
                    <h5 className="card-header">Token Info</h5>
                    <div className="card-body">
                        <div className="mb-3">
                            <label htmlFor="token-address" className="form-label">Address</label>
                            <div className="input-group">
                                <input
                                    readOnly={true}
                                    value={address}
                                    id="token-address"
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter CW20 token address"
                                />
                            </div>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="token-name" className="form-label">Name</label>
                            <input
                                value={tokenInfo?.name}
                                className="form-control"
                                type="text"
                                id="token-name"
                                placeholder="Token name"
                                readOnly={true}
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="token-symbol" className="form-label">Symbol</label>
                            <input
                                value={tokenInfo?.symbol}
                                className="form-control"
                                type="text"
                                id="token-symbol"
                                placeholder="Token symbol"
                                readOnly={true}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12 col-md-6">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <h5 className="card-header">Staking Configuration</h5>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label htmlFor="staking-apr" className="form-label">APR</label>
                                    <div className="input-group">
                                        <input
                                            onChange={({ target }) => setApr(target.value)}
                                            value={apr}
                                            type="number"
                                            min={0}
                                            max={100}
                                            className="form-control"
                                            id="staking-apr"
                                            placeholder="Enter APR"
                                        />
                                        <span className="input-group-text">%</span>
                                    </div>
                                    <div className="form-text">
                                        Enter the Annual Percentage Rate, use . (period) as decimal separator
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="staking-apr" className="form-label">Unstaking Duration</label>
                                    <div className="input-group">
                                        <input
                                            onChange={({ target }) => setDuration(target.value)}
                                            value={duration}
                                            min="1"
                                            step="1"
                                            type="number"
                                            className="form-control"
                                            id="staking-apr"
                                            placeholder="Enter unstaking duration"
                                        />
                                        <span className="input-group-text">Days</span>
                                    </div>
                                    <div className="form-text">
                                        Enter the duration for unstaking tokens, min. 1 day
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="token-address" className="form-label">Owner Address</label>
                                    <div className="input-group">
                                        <input
                                            value={ownerAddress}
                                            onChange={({ target }) => setOwnerAddress(target.value)}
                                            id="token-address"
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter CW20 token address"
                                        />
                                        <button onClick={() => paste(setOwnerAddress)} className="btn btn-outline-primary" type="button">Paste</button>
                                    </div>
                                    <div className="form-text">
                                        Enter the owner address, owner can increase and decrease staking pool
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 mt-4">
                        <div className="card">
                            <h5 className="card-header">Change Config</h5>
                            <div className="card-body">
                                {
                                    isValid() && <div className="mb-3 col-12 mb-0">
                                        <div className="alert alert-danger">
                                            <p className="mb-0">
                                                {isValid()}
                                            </p>
                                        </div>
                                    </div>
                                }

                                <button
                                    disabled={isValid() != undefined}
                                    onClick={onRegister}
                                    className="btn btn-primary deactivate-account">Change Config</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
}